'use client';

import React, { useEffect, useRef, useState } from 'react';
import styles from './sso.module.scss';
import { Button, Icon, validIcons, ButtonSize, ButtonType, Loader, Toast } from '@brahma/components';
import { SubHeader } from '@brahma/components';
import { useFetch, useLoadingRouter } from '@brahma/hooks';
import { interpolateString } from '@brahma/utils';
import { API_URLS } from '$constants/apis';
import { useSearchParams, usePathname } from 'next/navigation';
import { SEARCH_PARAMS_KEYS } from '$training-portal/constants';
import useGlobalContext from '$training-portal/hooks/use-global-context';
import { ContextAction } from '$training-portal/context/global-context/types';
import { getRedirectUrl } from '$training-portal/modules/onboarding/module-components/helpers';
import type { loginUrlResponse, RootObject } from './ssoLogin.types';
import { SSO_LOGIN_CONSTANTS, SSO_LOGIN_ERROR_CONSTANTS } from '../onboarding.constants';
const SsoLogin = () => {
  const {
    routerPush,
    loading: pageLoading
  } = useLoadingRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const prevCode = useRef('');
  const code = searchParams.get(SEARCH_PARAMS_KEYS.ssoLoginCode);
  const [newUserRegister, setNewUserRegister] = useState<boolean>(false);
  const globalContext = useGlobalContext();
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [authorizationError, setAuthorizationError] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setRedirectUrl(window.location.toString().replace(window.location.search, ''));
    }
  }, []);
  const {
    data: loginUrlResponse,
    loading: loginUrlLoading,
    fetch: loginUrlFetch
  } = useFetch<loginUrlResponse>({
    url: interpolateString(API_URLS.getSsoLoginUrl, {
      redirectUrl
    }),
    options: {
      headers: {
        'Component-Name': 'SsoLogin'
      }
    }
  }, false);
  const {
    data: ssoLoginResponse,
    fetch: ssoLoginFetch,
    loading: loadingSSo,
    error: ssoLoginError
  } = useFetch<RootObject>({
    url: interpolateString(API_URLS.ssoLogin, {
      code: code ?? '',
      redirectUrl: redirectUrl
    })
  }, false);
  useEffect(() => {
    if (code && prevCode.current !== code && redirectUrl) {
      ssoLoginFetch();
      prevCode.current = code;
    }
    if (!code && redirectUrl) {
      loginUrlFetch();
    }
  }, [code, redirectUrl]);
  useEffect(() => {
    if (ssoLoginError?.error?.httpStatusCode) {
      if (ssoLoginError.error.httpStatusCode !== 401) {
        // in case of not authorized error, get redirect URL again
        loginUrlFetch();
      }
    }
  }, [ssoLoginError]);
  useEffect(() => {
    if (ssoLoginResponse?.action === SSO_LOGIN_CONSTANTS.RELOAD) {
      loginUrlFetch();
      setNewUserRegister(true);
    } else {
      if (ssoLoginResponse?.user?.roles.length !== 0) {
        setAuthorizationError(false);
        if (ssoLoginResponse?.user?.id) {
          setNewUserRegister(false);
          globalContext.dispatch({
            type: ContextAction.setUser,
            payload: {
              ...ssoLoginResponse?.user,
              userGroups: ssoLoginResponse.user.userGroups ?? []
            }
          });
          let path = `/${pathname.includes('trainer') ? 'trainer' : 'trainee'}/${getRedirectUrl(ssoLoginResponse.user)}`;
          routerPush(path);
        }
      } else {
        setAuthorizationError(true);
      }
    }
  }, [ssoLoginResponse]);
  useEffect(() => {
    if (loginUrlResponse?.ssoUrl && newUserRegister) {
      routerPush(loginUrlResponse?.ssoUrl);
    }
  }, [loginUrlResponse, newUserRegister]);
  const onSigninClicked = () => {
    if (loginUrlResponse?.ssoUrl) {
      routerPush(loginUrlResponse?.ssoUrl);
    }
  };
  return <div className={styles.parentContainer} data-sentry-component="SsoLogin" data-sentry-source-file="index.tsx">
      <Loader show={loadingSSo || loginUrlLoading || pageLoading} data-sentry-element="Loader" data-sentry-source-file="index.tsx" />
      <div className={styles.toastContainer}>
        <Toast size="lg" isOpen={authorizationError} variant={SSO_LOGIN_ERROR_CONSTANTS.error} content={SSO_LOGIN_ERROR_CONSTANTS.Not_Authorized} autoCloseDuration={3000} onClose={() => window.location.reload()} data-sentry-element="Toast" data-sentry-source-file="index.tsx" />
      </div>
      <SubHeader iconName={validIcons.checkMarkShadow} title={SSO_LOGIN_CONSTANTS.HEADING1} className={styles.ssoTPSubHeader} description={SSO_LOGIN_CONSTANTS.SUB_HEADING} iconClassName={styles.iconSubheader} iconWidth="144" background="gridDotMdDesktop" baseUrl={process.env.NEXT_PUBLIC_STATIC_URL ?? ''} data-sentry-element="SubHeader" data-sentry-source-file="index.tsx" />
      <Button text={SSO_LOGIN_CONSTANTS.SIGN_IN} variant={ButtonType.Secondary} size={ButtonSize.Large} startIcon={<Icon iconName={validIcons.google} />} className={styles.buttonBox} onClick={onSigninClicked} stopThrottle={true} data-sentry-element="Button" data-sentry-source-file="index.tsx" />
    </div>;
};
export default SsoLogin;